import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { CircularProgress, Box } from '@mui/material'
import Layout from '@/components/layout/Layout'
import Home from '@/pages/Home'
import News from '@/pages/News'
import GsaCalifornia from '@/pages/GsaCalifornia'
import ForGsas from '@/pages/ForGsas'
import GsaData from '@/pages/GsaData'
import GSADetailPage from '@/pages/GSADetailPage'
import PublicationDetail from '@/pages/PublicationDetail'
import TenantPage from '@/pages/TenantPage'
import NotFound from '@/pages/NotFound'
import { apiUrl } from '@/lib/apiBase'

/**
 * Legacy newsletter detail → canonical News detail (epic e9 / T-114).
 *
 * The legacy ``/newsletters/:id`` path used the newsletter_id. The canonical
 * detail route is keyed on publication_id, so we resolve the newsletter to its
 * canonical publication via the legacy detail endpoint (which now returns the
 * canonical ``publication_id``) and redirect, preserving the deep link without
 * losing the specific item. Falls back to the archive if it can't be resolved.
 */
function LegacyNewsletterRedirect() {
  const { id } = useParams<{ id: string }>()
  const [target, setTarget] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setTarget('/news')
      return
    }
    const controller = new AbortController()
    fetch(apiUrl(`/api/agro/v1/newsletters/${id}`), { signal: controller.signal })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: { publication_id?: number | null }) => {
        setTarget(
          data.publication_id != null ? `/news/${data.publication_id}` : '/news',
        )
      })
      .catch(() => setTarget('/news'))
    return () => controller.abort()
  }, [id])

  if (target === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 16 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }
  return <Navigate to={target} replace />
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/"                       element={<Home />} />

        {/* Canonical News surface — articles, backed by the publication API (epic e9) */}
        <Route path="/news"                   element={<News />} />
        <Route path="/news/:id"               element={<PublicationDetail />} />

        {/* Legacy /publications aliases → canonical /news (kept valid, no dead links) */}
        <Route path="/publications"           element={<Navigate to="/news" replace />} />
        <Route path="/publications/:id"       element={<PublicationRedirect />} />

        {/* Legacy newsletter aliases → canonical /news */}
        <Route path="/newsletters"            element={<Navigate to="/news" replace />} />
        <Route path="/newsletters/:id"        element={<LegacyNewsletterRedirect />} />

        <Route path="/gsa-california"         element={<GsaCalifornia />} />

        {/* GSA-facing offer page — positioning + newsletter/discovery CTA (ag-010) */}
        <Route path="/for-gsas"               element={<ForGsas />} />
        <Route path="/gsa-data"              element={<GsaData />} />
        <Route path="/gsa-data/:nameSlug"     element={<GSADetailPage />} />

        {/* Per-GSA branded pilot page — "pilot-in-a-box" (T-203) */}
        <Route path="/g/:slug"                element={<TenantPage />} />

        <Route path="*"                       element={<NotFound />} />
      </Route>
    </Routes>
  )
}

/** Legacy ``/publications/:id`` deep links → canonical ``/news/:id`` (same id). */
function PublicationRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/news/${id}` : '/news'} replace />
}
